import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { Container } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';
import { Form, Formik } from 'formik';
import validationSchema from './FormValidation/ValidationSchema';
import StepWindFacilityData from './FacilityData/StepWindFacilityData';
import StepIndicator from '../../common/StepIndicator';
import Button from '../../common/Button';
import LoadingSpinner from '../../common/LoadingSpinner';
import buttonStyles from '../../common/Button/Button.module.scss';
import { ArrowLeft2, ArrowLeft3 } from '../../assets/icons';
import s from './OfferGenerationWizard.module.scss';
import WHO_TILE_CONFIGS from './StepWho.config';
import PRODUCT_TILE_CONFIGS from './StepWhichProduct.config';
import FACILITY_TYPE_TILE_CONFIGS from './StepFacilityType.config';
import CERTIFICATE_TILE_CONFIGS from './StepCertificate.config';
import LARGE_SOLAR_FACILITY_TYPE_TILE_CONFIGS from './StepLargeSolarFacilityType.config';
import POWER_TYPE_TILE_CONFIGS from './StepPowerType.config';
import StepGenericTile from './StepGenericTile';
import StepSomeInformation from './StepSomeInformation';
import StepIndividualOffer from './StepIndividualOffer';
import StepWaterFacilityData from './FacilityData/StepWaterFacilityData';
import StepSolarFacilityData from './FacilityData/StepSolarFacilityData';
import StepBioMasseFacilityData from './FacilityData/StepBioMasseFacilityData';
import StepCo2Form from './CertificateForm/StepCo2Form';
import { errorToTouchedAndIds } from '../../common/FormFields/formUtils';
import StepProofOfOriginForm from './CertificateForm/StepProofOfOriginForm';
import StepLargeSolarFacilityData from './StepLargeSolarFacilityData';
import RequiredFieldInfo from './RequiredFieldInfo';
import StepGreenPowerData from './PowerDelivery/StepGreenPowerData';
import StepGreyPowerData from './PowerDelivery/StepGreyPowerData';
import { contactPageRoutePrefix } from '../../MainRouter/router.constants';
import StepBusinessChargingData from './StepBusinessChargingData';
import BUSINESS_CUSTOMER_PRODUCT_TILE_CONFIGS from './StepWhichBusinessCustomerProduct.config';
import StepImmoChargingData from './StepImmoChargingData';
import StepCoopChargingData from './StepCoopChargingData';

/**
 * Map tile config values to index
 * @param {Object} prev previous object
 * @param {Object} value the value in the tile config array
 * @param {number} index the index in the tile config array
 * @returns {Object} the object value
 */
function mapTileConfigIndex(prev, value, index) {
  const p = prev;
  p[value.value] = index;
  return p;
}

const LAST_TWO_STEPS = [
  {
    componentClass: StepSomeInformation,
    fieldName: 'information',
    stepIndicatorLabel: 'stepindicator.label.information',
    noMapNeeded: true,
    props: {},
    urlValue: 'Angaben',
    nodes: [
      {
        componentClass: StepIndividualOffer,
        stepIndicatorLabel: 'stepindicator.label.individualOffer',
        props: {},
        urlValue: 'Zusammenfassung',
      },
    ],
  },
];

const WIZARD_STRUCTURE = {
  componentClass: StepGenericTile,
  fieldName: 'category',
  stepIndicatorLabel: 'stepindicator.label.category',
  props: {
    name: 'category',
    msgId: 'offerWizard.stepWho.title',
    introMsgId: 'offerWizard.stepWho.intro',
    tileConfigs: WHO_TILE_CONFIGS,
  },
  map: {
    gewerbekunde: 0,
    energieversorger: 1,
    industriekunde: 1,
    anlagenbetreiber: 1,
  },
  noMapNeeded: false,
  nodes: [
    // Gewerbekunde Produktauswahl
    {
      fieldName: 'product',
      stepIndicatorLabel: 'stepindicator.label.product',
      props: {
        name: 'product',
        msgId: 'offerWizard.stepWhichBusinessCustomerProduct.title',
        tileConfigs: BUSINESS_CUSTOMER_PRODUCT_TILE_CONFIGS,
      },
      componentClass: StepGenericTile,
      map: BUSINESS_CUSTOMER_PRODUCT_TILE_CONFIGS.reduce(
        mapTileConfigIndex,
        {}
      ),
      noMapNeeded: false,
      nodes: [
        {
          // step 3 is skipped for immo charging
          componentClass: null,
          skipThisStep: true,
          noMapNeeded: true,
          props: {},
          nodes: [
            // Immo-Charging
            {
              componentClass: StepImmoChargingData,
              fieldName: 'immoChargingData',
              stepIndicatorLabel: 'stepindicator.label.immoChargingData',
              noMapNeeded: true,
              props: {},
              nodes: LAST_TWO_STEPS,
            },
          ],
        },
        {
          // step 3 is skipped for coop charging
          componentClass: null,
          skipThisStep: true,
          noMapNeeded: true,
          props: {},
          nodes: [
            // Coop-Charging
            {
              componentClass: StepCoopChargingData,
              fieldName: 'coopChargingData',
              stepIndicatorLabel: 'stepindicator.label.coopChargingData',
              noMapNeeded: true,
              props: {},
              nodes: LAST_TWO_STEPS,
            },
          ],
        },
      ],
    },
    // Produktauswahl
    {
      componentClass: StepGenericTile,
      fieldName: 'product',
      stepIndicatorLabel: 'stepindicator.label.product',
      props: {
        name: 'product',
        msgId: 'offerWizard.stepWhichProduct.title',
        tileConfigs: PRODUCT_TILE_CONFIGS,
      },
      map: PRODUCT_TILE_CONFIGS.reduce(mapTileConfigIndex, {}),
      noMapNeeded: false,
      nodes: [
        {
          // Stromlieferung
          componentClass: StepGenericTile,
          fieldName: 'powerType',
          stepIndicatorLabel: 'stepindicator.label.powerType',
          map: POWER_TYPE_TILE_CONFIGS.reduce(mapTileConfigIndex, {}),
          props: {
            name: 'powerType',
            msgId: 'offerWizard.stepPowerType.title',
            tileConfigs: POWER_TYPE_TILE_CONFIGS,
          },
          noMapNeeded: false,
          nodes: [
            {
              componentClass: StepGreenPowerData,
              fieldName: 'greenPowerData',
              stepIndicatorLabel: 'stepindicator.label.greenPowerData',
              props: {},
              noMapNeeded: true,
              nodes: LAST_TWO_STEPS,
            },
            {
              componentClass: StepGreyPowerData,
              fieldName: 'greyPowerData',
              stepIndicatorLabel: 'stepindicator.label.greyPowerData',
              props: {},
              noMapNeeded: true,
              nodes: LAST_TWO_STEPS,
            },
          ],
        },
        {
          // Zertifikate
          componentClass: StepGenericTile,
          fieldName: 'certificate',
          stepIndicatorLabel: 'stepindicator.label.certificate',
          props: {
            name: 'certificate',
            msgId: 'offerWizard.StepCertificate.title',
            tileConfigs: CERTIFICATE_TILE_CONFIGS,
          },
          map: CERTIFICATE_TILE_CONFIGS.reduce(mapTileConfigIndex, {}),
          nodes: [
            {
              // CO2
              componentClass: StepCo2Form,
              fieldName: 'co2CertificateData',
              stepIndicatorLabel: 'stepindicator.label.co2CertificateData',
              noMapNeeded: true,
              props: {},
              nodes: LAST_TWO_STEPS,
            },
            {
              // Herkunftsnachweis
              componentClass: StepProofOfOriginForm,
              fieldName: 'proofOfOriginCertificateData',
              stepIndicatorLabel:
                'stepindicator.label.proofOfOriginCertificateData',
              noMapNeeded: true,
              props: {},
              nodes: LAST_TWO_STEPS,
            },
          ],
        },
        {
          // Direktvermarktung
          componentClass: StepGenericTile,
          fieldName: 'facilityType',
          stepIndicatorLabel: 'stepindicator.label.facilityType',
          map: FACILITY_TYPE_TILE_CONFIGS.reduce(mapTileConfigIndex, {}),
          noMapNeeded: false,
          props: {
            name: 'facilityType',
            msgId: 'offerWizard.stepFacilityType.title',
            tileConfigs: FACILITY_TYPE_TILE_CONFIGS,
          },
          nodes: [
            {
              // Wind
              componentClass: StepWindFacilityData,
              fieldName: 'windFacilityData',
              stepIndicatorLabel: 'stepindicator.label.windFacilityData',
              noMapNeeded: true,
              props: {},
              nodes: LAST_TWO_STEPS,
            },
            {
              // Wasser
              componentClass: StepWaterFacilityData,
              fieldName: 'waterFacilityData',
              stepIndicatorLabel: 'stepindicator.label.waterFacilityData',
              noMapNeeded: true,
              props: {},
              nodes: LAST_TWO_STEPS,
            },
            {
              // Photovoltaik
              componentClass: StepSolarFacilityData,
              fieldName: 'solarFacilityData',
              stepIndicatorLabel: 'stepindicator.label.solarFacilityData',
              noMapNeeded: true,
              props: {},
              nodes: LAST_TWO_STEPS,
            },
            {
              // Biomasse
              componentClass: StepBioMasseFacilityData,
              fieldName: 'bioMasseFacilityData',
              stepIndicatorLabel: 'stepindicator.label.bioMasseFacilityData',
              noMapNeeded: true,
              props: {},
              nodes: LAST_TWO_STEPS,
            },
          ],
        },
        {
          // Photovoltaik Grossanlage
          componentClass: StepGenericTile,
          fieldName: 'largeSolarFacilityType',
          stepIndicatorLabel: 'stepindicator.label.largeSolarFacilityType',
          noMapNeeded: true,
          props: {
            name: 'largeSolarFacilityType',
            msgId: 'offerWizard.stepLargeSolarFacility.title.desktop',
            mobileHeaderMsgId:
              'offerWizard.stepLargeSolarFacility.title.mobile',
            tileConfigs: LARGE_SOLAR_FACILITY_TYPE_TILE_CONFIGS,
          },
          nodes: [
            {
              componentClass: StepLargeSolarFacilityData,
              fieldName: 'largeSolarFacilityData',
              stepIndicatorLabel: 'stepindicator.label.largeSolarFacilityData',
              noMapNeeded: true,
              props: {},
              nodes: LAST_TWO_STEPS,
            },
          ],
        },
        {
          // step 3 is skipped for business charging
          componentClass: null,
          skipThisStep: true,
          noMapNeeded: true,
          props: {},
          nodes: [
            {
              // Business Charging
              componentClass: StepBusinessChargingData,
              fieldName: 'businessChargingData',
              stepIndicatorLabel: 'stepindicator.label.businessChargingData',
              noMapNeeded: true,
              props: {},
              nodes: LAST_TWO_STEPS,
            },
          ],
        },
      ],
    },
  ],
};

function getPathAndFormValueFromWizardNodeAndUrl(pathUrlComponent, wizardNode) {
  let pathValue = null;
  let formValue = null;
  const { componentClass } = wizardNode;
  if (componentClass === StepGenericTile) {
    const { tileConfigs } = wizardNode.props;
    const { fieldName } = wizardNode;
    const tileOption = tileConfigs.find(
      (tc) => tc.urlValue === pathUrlComponent
    );
    if (tileOption) {
      formValue = {
        [fieldName]: tileOption.value,
      };
      if (wizardNode.noMapNeeded) {
        pathValue = 0;
      } else {
        pathValue = wizardNode.map[tileOption.value];
      }
    }
  } else {
    const { nodes } = wizardNode;
    pathValue = nodes.findIndex((n) => n.urlValue === pathUrlComponent);
    if (pathValue < 0) {
      pathValue = null;
    }
  }
  return { pathValue, formValue };
}

/**
 * Get the form values, current step and current path from location (route)
 * Needed for routing users to the correct step when they enter from a direct link
 * @param {Object} location - the location from react router
 * @param {Object} initialValues - initial form values
 * @returns {Array} - values, step and path
 */
function getFormStepAndPathFromLocation(location, initialValues = false) {
  let stepFromLocation = 0;
  let pathFromLocation = [];
  let formValues = {};

  const { pathname } = location;
  if (pathname.startsWith(contactPageRoutePrefix)) {
    let pathWithoutPrefix = pathname.slice(contactPageRoutePrefix.length);
    if (pathWithoutPrefix.startsWith('/')) {
      pathWithoutPrefix = pathWithoutPrefix.slice(1);
    }
    const pathUrlComponents = pathWithoutPrefix.split('/');

    let currentNode = WIZARD_STRUCTURE;
    for (let i = 0; i < pathUrlComponents.length; i += 1) {
      const pathUrlComponent = pathUrlComponents[i];
      const { pathValue, formValue } = getPathAndFormValueFromWizardNodeAndUrl(
        pathUrlComponent,
        currentNode
      );
      if (pathValue == null) {
        break;
      }
      pathFromLocation.push(pathValue);
      stepFromLocation += 1;
      currentNode = currentNode.nodes[pathValue];

      if (currentNode.skipThisStep) {
        stepFromLocation += 1;
        pathFromLocation.push(0);
        [currentNode] = currentNode.nodes;
      }

      if (formValue !== null) {
        formValues = {
          ...formValues,
          ...formValue,
        };
      }
    }
  }

  if (initialValues) {
    if (stepFromLocation > 3) {
      stepFromLocation = 3;
      pathFromLocation = pathFromLocation.slice(0, 3);
    }
  }

  return [formValues, stepFromLocation, pathFromLocation];
}

/**
 * Get the location (route) for a given step and path
 * @param {number} currentStep - the step in the wizard
 * @param {number[]} currentPath - the path in the wizard tree e.g. [0, 2, 1]
 * @param {Object} formValues - current formik form values
 * @returns {string} - the location
 */
function getNewLocationFromState(currentStep, currentPath, formValues) {
  const urlComponents = [];
  let currentNode = WIZARD_STRUCTURE;
  for (let i = 0; i < currentStep; i += 1) {
    const { componentClass } = currentNode;
    if (!currentNode.skipThisStep) {
      if (componentClass === StepGenericTile) {
        const { tileConfigs } = currentNode.props;
        const { fieldName } = currentNode;
        const fieldValue = formValues[fieldName];
        const tileOption = tileConfigs.find((tc) => tc.value === fieldValue);
        if (tileOption) {
          urlComponents.push(tileOption.urlValue);
        } else {
          break;
        }
      } else {
        const { nodes } = currentNode;
        if (Array.isArray(nodes) && nodes.length) {
          urlComponents.push(nodes[0].urlValue);
        } else {
          break;
        }
      }
    }
    currentNode = currentNode.nodes[currentPath[i]];
  }

  let urlPrefix = contactPageRoutePrefix;
  if (urlComponents.length === 0) {
    return urlPrefix;
  }
  if (!urlPrefix.endsWith('/')) {
    urlPrefix = `${urlPrefix}/`;
  }
  return `${urlPrefix}${urlComponents.join('/')}`;
}

function getNextOrBackFromLocationDiff(stateLocation, pathname) {
  if (
    !stateLocation.startsWith(contactPageRoutePrefix) ||
    !pathname.startsWith(contactPageRoutePrefix)
  ) {
    return null;
  }

  const current = stateLocation.slice(contactPageRoutePrefix.length);
  const pending = stateLocation.slice(contactPageRoutePrefix.length);

  if (current.startsWith(pending)) {
    return 'back';
  }
  if (pending.startsWith(current)) {
    return 'next';
  }

  return null;
}

/**
 * Find the matching node in the wizard structure for a given path and step
 * @param {number} currentStep - the step in the wizard
 * @param {number[]} currentPath - the path in the wizard tree e.g. [0, 2, 1]
 * @returns Object - the matching node
 */
function findWizardStructureNode(currentStep, currentPath) {
  let currentNode = WIZARD_STRUCTURE;
  for (let i = 0; i < currentStep; i += 1) {
    if (!currentNode || !currentNode.nodes) return null;
    currentNode = currentNode.nodes[currentPath[i]];
  }
  return currentNode;
}

/**
 * Get component class and props for a step and path
 * @param {number} currentStep - the currenct step in the wizard
 * @param {number[]} currentPath - current path in the wizard tree
 * @returns {Object} object wit ComponentClass and componentProps
 */
function getComponentClassAndProps(currentStep, currentPath) {
  const node = findWizardStructureNode(currentStep, currentPath);
  return node;
}

/**
 * Check if the given path is valid for the current form values.
 * @param {number} currentStep - the step in the wizard
 * @param {number[]} currentPath - path in the wizard tree
 * @param {Object} values - current formik form values
 * @returns {boolean} - true if the path is valid
 */
function validPathSelected(currentStep, currentPath, values) {
  const node = findWizardStructureNode(currentStep, currentPath);
  if (!node) return false;
  const { fieldName, map, noMapNeeded } = node;
  if (noMapNeeded) return true;
  if (!fieldName && currentStep === 3) return true; // Transition to StepSomeInformation
  const fieldValue = values[fieldName];
  return typeof (map && map[fieldValue]) === 'number';
}

function OfferGenerationWizard(props) {
  const { handleRequestOffer, intl } = props;
  const location = useLocation();
  const history = useHistory();
  const formikRef = React.useRef(null);
  const stepIndicatorRef = React.useRef(null);
  const [initialFormValues, initialStep, initialPath] =
    getFormStepAndPathFromLocation(location, true);
  /* current step: number */
  const [currentStep, setCurrentStep] = React.useState(initialStep);
  /* current path: array representing location in wizard structure e.g. [0, 2, 1] */
  const [currentPath, setCurrentPath] = React.useState(initialPath);
  const [submissionError, setSubmissionError] = React.useState(null);
  const [submitted, setSubmitted] = React.useState(false);
  const [isSolarFacilitySoldOut, setIsSolarFacilitySoldOut] =
    React.useState(false);
  const [isGreenPowerConsumptionTooSmall, setisGreenPowerConsumptionTooSmall] =
    React.useState(false);
  const [isGreyPowerConsumptionTooSmall, setIsGreyPowerConsumptionTooSmall] =
    React.useState(false);

  /* Needed for accessibility so that we don't move the focus before the user starts
  interacting with the wizard */
  const [isFirstDisplay, setIsFirstDisplay] = React.useState(true);

  const prevStepRef = React.useRef();
  const formikPropsRef = React.useRef();

  React.useEffect(() => {
    prevStepRef.current = currentStep;
  });
  const prevStep = prevStepRef.current;

  /**
   * Go to the previous step in the wizard
   * @param {Object} formikProps
   */
  const goBack = (formikProps) => {
    let newStep = currentStep - 1;
    const { skipThisStep } = findWizardStructureNode(newStep, currentPath);
    if (skipThisStep) {
      newStep -= 1;
    }
    formikProps.setFieldValue('step', newStep);
    setCurrentStep(newStep);
  };

  /**
   * Run the formik validation
   * @returns {boolean} - true if the currently entered values are all valid
   */
  const validateManually = async () => {
    let result = false;
    if (formikPropsRef.current) {
      const errors = await formikPropsRef.current.validateForm();
      if (
        errors &&
        errors.largeSolarFacilityData &&
        errors.largeSolarFacilityData.isSolarFacilitySoldOut
      ) {
        setIsSolarFacilitySoldOut(true);
      } else {
        setIsSolarFacilitySoldOut(false);
      }
      if (
        errors &&
        errors.greenPowerData &&
        errors.greenPowerData.isGreenPowerConsumptionTooSmall
      ) {
        setisGreenPowerConsumptionTooSmall(true);
      } else {
        setisGreenPowerConsumptionTooSmall(false);
      }

      if (
        errors &&
        errors.greyPowerData &&
        errors.greyPowerData.isGreyPowerConsumptionTooSmall
      ) {
        setIsGreyPowerConsumptionTooSmall(true);
      } else {
        setIsGreyPowerConsumptionTooSmall(false);
      }

      const { touchedObj, errorIds } = errorToTouchedAndIds(errors);
      formikPropsRef.current.setTouched(touchedObj);
      if (Object.keys(errors).length === 0) {
        result = true;
      } else if (errorIds.length > 0) {
        const selector = errorIds.map((k) => `[id='${k}']`).join(', ');
        const errorElements = document.querySelectorAll(selector);
        if (errorElements && errorElements.length) {
          errorElements[0].focus();
        }
        result = false;
      }
    }
    return result;
  };

  /**
   * Go to the next step in the wizard
   * @returns {undefined}
   */
  const goToNext = async () => {
    const node = findWizardStructureNode(currentStep, currentPath);
    const { fieldName, map, noMapNeeded } = node;

    const newPath = [...currentPath];
    let newStep = currentStep + 1;

    if (formikPropsRef.current) {
      const valid = await validateManually();
      if (!valid) {
        return;
      }
    }

    if (noMapNeeded) {
      newPath[currentStep] = 0;
    } else if (fieldName && map) {
      const fieldValue = formikPropsRef.current.values[fieldName];
      newPath[currentStep] = map[fieldValue];
    }

    const newNode = findWizardStructureNode(newStep, newPath);
    if (newNode.skipThisStep) {
      newStep += 1;
      newPath[currentStep + 1] = 0;
    }

    setCurrentPath(newPath);
    setCurrentStep(newStep);
    if (formikPropsRef.current) {
      formikPropsRef.current.setFieldValue('step', newStep);
    }
  };

  React.useEffect(() => {
    /* Accessibility: set the focus to the start of the newly loaded step */
    if (stepIndicatorRef && !isFirstDisplay && prevStep !== currentStep) {
      stepIndicatorRef.current.focus();
    }
    setIsFirstDisplay(false);
  }, [prevStep, currentStep, isFirstDisplay]);

  React.useEffect(() => {
    if (formikRef.current) {
      const formValues = formikRef.current.values;
      const newLocation = getNewLocationFromState(
        currentStep,
        currentPath,
        formValues
      );
      if (location.pathname !== newLocation) {
        if (currentStep === initialStep && currentPath === initialPath) {
          history.replace(newLocation);
        } else {
          history.push(newLocation);
        }
      }
    }
  }, [currentStep, currentPath]);

  React.useEffect(() => {
    if (
      formikRef.current &&
      !(currentStep === initialStep && currentPath === initialPath)
    ) {
      const { pathname } = location;
      const formValues = formikRef.current.values;
      const stateLocation = getNewLocationFromState(
        currentStep,
        currentPath,
        formValues
      );
      if (stateLocation !== pathname) {
        const direction = getNextOrBackFromLocationDiff(
          stateLocation,
          pathname
        );
        if (direction === 'back' && currentStep > 0) {
          goBack(formikRef.current);
        } else {
          history.replace(stateLocation);
        }
      }
    }
  }, [location.pathname]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmissionError(null);
    const error = await handleRequestOffer(values, setSubmitting);
    if (!error) {
      setSubmitted(true);
    } else {
      setSubmissionError(error);
    }
    setSubmitting(false);
  };

  const nextStepIsSubmit = currentStep === 5;
  const currentStepIsOffer = currentStep === 5;
  const currentStepIsFirstStep = currentStep === 0;
  const showRequiredFieldInfo = currentStep >= 3;
  const {
    componentClass: ComponentClass,
    props: componentProps,
    stepIndicatorLabel,
  } = getComponentClassAndProps(currentStep, currentPath);

  return (
    <Container tag="main" className={s.container}>
      <Formik
        innerRef={formikRef}
        initialValues={{
          windFacilityData: [{ id: uuidv4() }],
          waterFacilityData: [{ id: uuidv4() }],
          solarFacilityData: [{ id: uuidv4() }],
          bioMasseFacilityData: [{ id: uuidv4() }],
          information: {},
          individualOffer: {
            firstName: '',
            lastName: '',
            salutation: null,
            position: '',
            phone: '',
            email: '',
            privacyPolicy: false,
          },
          greenPowerData: {},
          greyPowerData: {},
          businessChargingData: {},
          immoChargingData: {},
          coopChargingData: {},
          largeSolarFacilityData: {},
          co2CertificateData: {},
          proofOfOriginCertificateData: {},
          step: initialStep,
          ...initialFormValues,
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema(intl)}
      >
        {(formikProps) => {
          const { values, isSubmitting, submitForm } = formikProps;
          formikPropsRef.current = formikProps;
          const isPathValid = validPathSelected(
            currentStep,
            currentPath,
            values
          );

          return (
            <Form autoComplete="on" noValidate>
              <div className={s.wizard}>
                <StepIndicator
                  total={6}
                  step={currentStep + 1}
                  className={s.stepIndicator}
                  ref={stepIndicatorRef}
                  label={intl.formatMessage({ id: stepIndicatorLabel })}
                />
                <ComponentClass
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...componentProps}
                  values={values}
                  form={formikProps}
                  submitted={submitted}
                  onNext={goToNext}
                  isSolarFacilitySoldOut={isSolarFacilitySoldOut}
                  isGreenPowerConsumptionTooSmall={
                    isGreenPowerConsumptionTooSmall
                  }
                  isGreyPowerConsumptionTooSmall={
                    isGreyPowerConsumptionTooSmall
                  }
                />
                {!currentStepIsOffer && (
                  <div className={s.wizardNavigation}>
                    {!currentStepIsFirstStep && (
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => goBack(formikProps)}
                      >
                        <FormattedMessage id="offerWizard.backButton" />
                      </Button>
                    )}
                    {/* We can't use our Button component for the submit button, because for some
                        reason the form immediately submits on render if we use a <Button> */}
                    {nextStepIsSubmit ? ( // currently dead code
                      <>
                        <button
                          type="submit"
                          className={`${buttonStyles.button} ${buttonStyles.primary}`}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            'Angebot wird erstellt...'
                          ) : (
                            <FormattedMessage id="offerWizard.nextButton" />
                          )}
                        </button>
                        {submissionError && (
                          <p style={{ color: 'red' }}>
                            Fehler bei der Angebotserstellung
                          </p>
                        )}
                      </>
                    ) : (
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      <>
                        {currentStep > 2 && (
                          <Button
                            disabled={!isPathValid}
                            variant="primary"
                            type="button"
                            onClick={() => goToNext()}
                          >
                            <FormattedMessage id="offerWizard.nextButton" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
                <div role="region" aria-live="polite">
                  {currentStepIsOffer && submitted && (
                    <div className={s.submitted}>
                      <FormattedMessage id="offerWizard.submitted" />
                    </div>
                  )}
                </div>
                {currentStepIsOffer && (
                  <div className={s.individualOfferButtons}>
                    <Button
                      variant="textOnly"
                      type="button"
                      onClick={() => goBack(formikProps)}
                      className={s.changeMyDataButton}
                      disabled={submitted || isSubmitting}
                    >
                      {submitted || isSubmitting ? (
                        <ArrowLeft3 />
                      ) : (
                        <ArrowLeft2 />
                      )}
                      <span>
                        <FormattedMessage id="offerWizard.StepIndividualOffer.changeMyData" />
                      </span>
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      className={s.requestOfferButton}
                      disabled={submitted || isSubmitting}
                      onClick={async () => {
                        await validateManually();
                        await submitForm();
                      }}
                    >
                      <div className={s.requestOfferButtonContent}>
                        <div>
                          <FormattedMessage id="offerWizard.StepIndividualOffer.requestIndividualOffer" />
                        </div>
                        <div
                          role="region"
                          aria-live="polite"
                          className={s.loadingSpinner}
                        >
                          {isSubmitting && <LoadingSpinner color="grey" />}
                        </div>
                      </div>
                    </Button>
                  </div>
                )}
                {showRequiredFieldInfo && <RequiredFieldInfo />}
              </div>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
}

OfferGenerationWizard.defaultPropts = {
  offer: null,
};

OfferGenerationWizard.propTypes = {
  handleRequestOffer: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired,
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

export default injectIntl(OfferGenerationWizard);
